import uniq from 'lodash/uniq'
import without from 'lodash/without'
import { EditToolState } from './state'
import { CallbackStatus } from '@/modules/Editor/callbackHandler'
import { EditorCursor, selectCursor } from '@/modules/Editor/editorCursor'
import { AnnotationManagerEvents, ToolEvents } from '@/modules/Editor/eventBus'
import type { IPoint } from '@/modules/Editor/point'
import { subPoints } from '@/modules/Editor/point'
import { ToolName, type ToolOption, type ToolOptionId } from '@/modules/Editor/tools/types'
import type { PointerEvent } from '@/core/utils/touch'
import { resolveEventPoint } from '@/core/utils/touch'
import { restoreAllJointsAction, updateAnnotationsData } from '@/modules/Editor/actions'
import type { SharedToolState, Tool, ToolContext } from '@/modules/Editor/managers/toolManager'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import { clearPath2DCache } from '@/modules/Editor/models/annotation/annotationRenderingCache'
import translateEyeVertex from '@/modules/Editor/plugins/eye/utils/translateVertex'
import { setupMouseButtonLoadout } from '@/modules/Editor/plugins/mixins/loadouts'
import { setupPanning } from '@/modules/Editor/plugins/mixins/setupPanning'
import { setupToolMouseCallbacks } from '@/modules/Editor/plugins/mixins/setupToolMouseCallbacks'
import {
  handlePolygonSubtract,
  hasMovedSignificantly,
  translatePath,
  translateVertex,
} from './utils'
import { isMultiSelectEvent } from '@/core/utils/event'
import type { View } from '@/modules/Editor/views/view'
import { MainAnnotationType } from '@/core/annotationTypes'

class EditTool implements Tool {
  /**
   * Comprehensive state machine that manages all EditTool state and logic
   */
  private state = new EditToolState()

  /**
   * Shared state for panning - exposed for external tool coordination
   */
  get sharedState(): SharedToolState {
    return this.state.sharedState
  }

  /**
   * Return an active tool option of current tool
   */
  activeToolOption(context: ToolContext): ToolOption | null {
    const { currentTool: tool } = context.editor.toolManager
    if (!tool || tool.name !== 'edit_tool') {
      return null
    }

    const activeOption = (tool.toolConfig.toolOptions || []).find((option) => option.active)
    if (!activeOption) {
      return null
    }

    return activeOption
  }

  deactivateToolOption(context: ToolContext, toolOptionId: ToolOptionId): void {
    ToolEvents.deactivateToolOption.emit({
      toolName: ToolName.Edit,
      lastHotkeyPressed: null,
      editing: false,
    })
    context.editor.toolManager.deactivateToolOption(toolOptionId)
  }

  deactivateToolOptions(context: ToolContext): void {
    ToolEvents.deactivateToolOptions.emit({
      toolName: ToolName.Edit,
      lastHotkeyPressed: null,
      editing: false,
    })
    context.editor.toolManager.deactivateToolOptions()
  }

  activate(context: ToolContext): void {
    selectCursor(EditorCursor.Move)

    context.editor.activeView.annotationsLayer.draw()

    setupMouseButtonLoadout(context, { middle: true })

    context.editor.registerCommand('edit_tool.restore_joints', () => {
      const { selectedAnnotation } = context.editor.activeView.annotationManager
      if (!selectedAnnotation) {
        return
      }
      if (!['skeleton', 'eye'].includes(selectedAnnotation.type)) {
        return
      }

      const action = restoreAllJointsAction(context.editor.activeView, selectedAnnotation)
      context.editor.actionManager.do(action)

      return CallbackStatus.Stop
    })

    context.editor.registerCommand('edit_tool.activate_polygon_merge', () => {
      if (context.editor.activeView.isLoading) {
        return
      }
      context.editor.toolManager.activateToolOption('polygon_merge')
      const { selectedAnnotation } = context.editor.activeView.annotationManager
      if (selectedAnnotation?.type === 'polygon') {
        ToolEvents.polygonMerge.emit({
          toolName: ToolName.Edit,
          lastHotkeyPressed: null,
          editing: false,
        })
      }
    })

    context.editor.registerCommand('edit_tool.activate_polygon_subtract', () => {
      if (context.editor.activeView.isLoading) {
        return
      }
      context.editor.toolManager.activateToolOption('polygon_subtract')
      const { selectedAnnotation } = context.editor.activeView.annotationManager
      if (selectedAnnotation?.type === 'polygon') {
        ToolEvents.polygonSubtract.emit({
          toolName: ToolName.Edit,
          lastHotkeyPressed: null,
          editing: false,
        })
      }
    })

    setupToolMouseCallbacks(
      context,
      this.onStart.bind(this),
      this.onMove.bind(this),
      this.onEnd.bind(this),
    )

    // goes at the end, since the tool can also be used to drag an annotation or a vertex
    // putting it at the end mans the tool hadlers fire off first
    setupPanning.primary(context, this.sharedState)
  }

  /**
   * Handles mouse down events in the Edit tool
   *
   * Responsibilities:
   * 1. Detects what's under the cursor (annotation, vertex, or empty area)
   * 2. Starts CMD+drag rectangle selection when no annotation is targeted
   * 3. Handles annotation selection/deselection with multi-select support
   * 4. Initiates annotation or vertex movement for selected items
   *
   * Note: Panning is handled separately by setupPanning.primary()
   */
  onStart(context: ToolContext, event: PointerEvent): void | CallbackStatus {
    const canvasPoint = resolveEventPoint(event)
    if (!canvasPoint) {
      return
    }

    const imagePoint = context.editor.activeView.camera.canvasViewToImageView(canvasPoint)

    // Store initial positions for potential use
    this.state.setInitialMousePosition(imagePoint)

    // Handle CMD+drag rectangle selection
    if (isMultiSelectEvent(event)) {
      return this.handleCmdClickStart(context, event, canvasPoint, imagePoint)
    }

    // For regular clicks, do immediate synchronous hit detection to decide panning
    return this.handleRegularClickStart(context, imagePoint)
  }

  /**
   * Handles CMD+click/drag start for multi-select rectangle mode
   */
  private handleCmdClickStart(
    context: ToolContext,
    event: PointerEvent,
    canvasPoint: IPoint,
    imagePoint: IPoint,
  ): CallbackStatus {
    // Do immediate synchronous hit detection for simplicity
    Promise.all([
      context.editor.activeView.annotationsLayer.hitItemRegion(imagePoint),
      context.editor.activeView.rasterAnnotationLayer.hitMaskRegion(imagePoint),
    ]).then(([hitItemId, hitMaskId]) => {
      const targetAnnotationId = hitItemId || hitMaskId
      this.state.targetedItemId = targetAnnotationId

      if (targetAnnotationId) {
        // CMD+click on annotation = immediate multi-select toggle
        // No rectangle selection when starting on annotation (simplified approach)
        this.state.resetRectangleSelection()

        const annotation =
          context.editor.activeView.annotationManager.getAnnotation(targetAnnotationId)
        if (annotation) {
          const currentSelection =
            context.editor.activeView.annotationManager.selectedAnnotations.map((a) => a.id)
          const isAlreadySelected =
            context.editor.activeView.annotationManager.isSelected(targetAnnotationId)

          if (isAlreadySelected) {
            // Remove from selection (deselect)
            const newSelection = currentSelection.filter((id) => id !== targetAnnotationId)
            context.editor.activeView.annotationManager.selectAnnotations(newSelection)
          } else {
            // Add to selection (select)
            const newSelection = [...currentSelection, targetAnnotationId]
            context.editor.activeView.annotationManager.selectAnnotations(newSelection)
          }
        }
      } else {
        // CMD+click/drag on empty space = start rectangle selection
        const currentSelection =
          context.editor.activeView.annotationManager.selectedAnnotations.map((a) => a.id)
        this.state.startRectangleSelection(canvasPoint, currentSelection)
      }
    })

    // Always stop panning for CMD+click/drag
    return CallbackStatus.Stop
  }

  /**
   * Handles regular click start (non-CMD)
   * Allows panning to start but can transition to annotation interaction if annotation detected quickly
   */
  private handleRegularClickStart(context: ToolContext, imagePoint: IPoint): CallbackStatus {
    // Start async annotation detection
    Promise.all([
      context.editor.activeView.annotationsLayer.hitItemRegion(imagePoint),
      context.editor.activeView.rasterAnnotationLayer.hitMaskRegion(imagePoint),
    ]).then(([hitItemId, hitMaskId]) => {
      const targetedItemId = hitItemId || hitMaskId
      this.state.targetedItemId = targetedItemId

      if (!targetedItemId) {
        // No annotation found - always clear selection for clicks on empty areas
        // This ensures blank space clicks work regardless of panning state
        context.editor.activeView.annotationManager.selectAnnotations([])
        this.deactivateToolOptions(context)
        return
      }

      const annotation = context.editor.activeView.annotationManager.getAnnotation(targetedItemId)
      if (!annotation) {
        // Invalid annotation - always clear selection
        context.editor.activeView.annotationManager.selectAnnotations([])
        this.deactivateToolOptions(context)
        return
      }

      // Found annotation - check if we should interrupt panning
      // Only handle annotation if panning hasn't started or hasn't moved much
      if (
        !this.sharedState.isPanning ||
        !hasMovedSignificantly(
          this.state.initialMouseDownPosition,
          this.state.previousMouseMovePosition,
        )
      ) {
        // Stop any panning that might have started
        if (this.sharedState.isPanning) {
          this.sharedState.isPanning = false
          // Force emit panning state change to notify other components
          ToolEvents.framePanningStateChange.emit(false)
        }

        this.handleAnnotationInteraction(context, annotation, imagePoint, false)
      }
      // If panning has moved significantly, let it continue and don't interfere
    })

    // Allow panning to proceed initially - annotation detection will handle interruption if needed
    return CallbackStatus.Continue
  }

  /**
   * Handles complex annotation interaction logic for selection and tool operations
   *
   * For selected annotations:
   * - Prepares for movement (annotation or vertex level)
   * - Handles multi-select deselection when CMD+clicking selected items
   *
   * For unselected annotations:
   * - Executes polygon merge/subtract operations if tool options are active
   * - Adds to selection (single or multi-select based on CMD key)
   *
   * @param context - Tool context with editor and view access
   * @param annotation - The annotation that was clicked
   * @param imagePoint - Click position in image coordinates
   * @param multiselectMode - Whether CMD/Ctrl key is pressed
   */
  private handleAnnotationInteraction(
    context: ToolContext,
    annotation: Annotation,
    imagePoint: IPoint,
    multiselectMode: boolean,
  ): CallbackStatus {
    const isSelected = context.editor.activeView.annotationManager.isSelected(annotation.id)
    if (!isSelected) {
      return this.handleUnselectedAnnotation(context, annotation, multiselectMode)
    }

    return this.handleSelectedAnnotation(context, annotation, imagePoint, multiselectMode)
  }

  /**
   * Handles interaction with an unselected annotation
   */
  private handleUnselectedAnnotation(
    context: ToolContext,
    annotation: Annotation,
    multiselectMode: boolean,
  ): CallbackStatus {
    const { selectedAnnotation } = context.editor.activeView.annotationManager
    const activeToolOption = this.activeToolOption(context)

    // Handle polygon tool operations (merge/subtract)
    if (
      activeToolOption &&
      annotation.type === 'polygon' &&
      selectedAnnotation?.type === 'polygon'
    ) {
      if (activeToolOption.id === 'polygon_merge') {
        AnnotationManagerEvents.mergePolygonAnnotations.emit(
          { viewId: context.editor.activeView.id },
          [selectedAnnotation.id, annotation.id],
        )
        this.deactivateToolOption(context, 'polygon_merge')
        return CallbackStatus.Stop
      }

      if (activeToolOption.id === 'polygon_subtract') {
        handlePolygonSubtract(selectedAnnotation, annotation, context)
        this.deactivateToolOption(context, 'polygon_subtract')
        context.editor.activeView.annotationManager.selectAnnotations([selectedAnnotation.id])
        return CallbackStatus.Stop
      }
    }

    // Regular annotation selection
    this.deactivateToolOptions(context)

    const selectedAnnotationIds = multiselectMode
      ? context.editor.activeView.annotationManager.selectedAnnotations.map((a) => a.id)
      : []
    context.editor.activeView.annotationManager.selectAnnotations(
      uniq([...selectedAnnotationIds, annotation.id]),
    )

    selectCursor(EditorCursor.Edit)
    return CallbackStatus.Stop
  }

  /**
   * Handles interaction with a selected annotation
   */
  private handleSelectedAnnotation(
    context: ToolContext,
    annotation: Annotation,
    imagePoint: IPoint,
    multiselectMode: boolean,
  ): CallbackStatus {
    this.deactivateToolOptions(context)

    // mask annotations cannot be moved
    if (annotation.type === MainAnnotationType.Mask) {
      return CallbackStatus.Stop
    }

    // Start tracking annotations for movement with their initial data
    this.state.startMovingAnnotationsWithData(
      context.editor.activeView.annotationManager.selectedAnnotations,
    )

    context.editor.activeView.annotationManager.deselectVertex()

    // Handle vertex selection for single annotation
    const getAnnotationById = (id: string) =>
      context.editor.activeView.annotationManager.getAnnotation(id)
    if (this.state.getMovingAnnotations(getAnnotationById).length === 1) {
      const vertex = context.editor.activeView.annotationManager.findAnnotationVertexAt(imagePoint)
      if (vertex) {
        // Calculate vertex index synchronously for the clicked annotation
        const targetedItemsIndex = context.editor.activeView.annotationsLayer.hitVertexRegion(
          annotation.id,
          imagePoint,
        )

        if (targetedItemsIndex !== undefined) {
          context.editor.activeView.annotationManager.selectVertexIndex(targetedItemsIndex)
          this.state.targetedItemsIndex = targetedItemsIndex
        }
        this.state.startMovingVertex(vertex)
      }
    }

    // Handle multiselect deselection
    if (multiselectMode) {
      const selectedAnnotationIds =
        context.editor.activeView.annotationManager.selectedAnnotations.map((a) => a.id)
      context.editor.activeView.annotationManager.selectAnnotations(
        uniq(without(selectedAnnotationIds, annotation.id)),
      )
    }

    selectCursor(EditorCursor.Edit)
    return CallbackStatus.Stop
  }

  /**
   * Detects what annotation (if any) is under the given point
   *
   * Hit detection priority:
   * 1. Vector annotations layer (always on top)
   * 2. Raster/mask annotations layer (underneath)
   *
   * @param view - The editor view containing the annotation layers
   * @param point - Position to check in image coordinates
   * @returns Promise resolving to annotation ID or undefined if nothing hit
   */
  private async getItemTargetIdFromPoint(view: View, point: IPoint): Promise<string | undefined> {
    const [hitItemId, hitMaskId] = await Promise.all([
      view.annotationsLayer.hitItemRegion(point),
      view.rasterAnnotationLayer.hitMaskRegion(point),
    ])

    return hitItemId || hitMaskId
  }

  /**
   * Handles mouse move events in the Edit tool
   *
   * Responsibilities:
   * 1. Updates CMD+drag rectangle selection drawing
   * 2. Moves selected annotations or individual vertices during drag
   * 3. Handles hover effects for annotations and vertices
   * 4. Updates cursor appearance based on what's under the mouse
   * 5. Highlights annotations and vertices on hover
   *
   * Note: Annotation movement takes priority over panning
   */
  onMove(context: ToolContext, event: PointerEvent): void | CallbackStatus {
    if (!context.editor.activeView.hitTarget(event)) {
      return CallbackStatus.Stop
    }

    const canvasPoint = resolveEventPoint(event)
    if (!canvasPoint) {
      return
    }

    // Handle rectangle drawing for CMD+drag multi-select (highest priority)
    if (this.state.isDrawingRectangle) {
      this.state.updateRectangleSelection(canvasPoint, context.editor.activeView)
      return CallbackStatus.Stop
    }

    const imagePoint = context.editor.activeView.camera.canvasViewToImageView(canvasPoint)
    const getAnnotationById = (id: string) =>
      context.editor.activeView.annotationManager.getAnnotation(id)
    const annotationsMoving = this.state.getMovingAnnotations(getAnnotationById)
    const previousMouseMovePosition = this.state.previousMouseMovePosition
    const vertexMoving = this.state.vertexMoving

    // Handle annotation/vertex movement (second highest priority - overrides panning)
    if (annotationsMoving.length === 1 && previousMouseMovePosition && vertexMoving) {
      // Moving vertex - stop any panning
      if (this.sharedState.isPanning) {
        this.sharedState.isPanning = false
      }

      if (
        annotationsMoving[0].annotation.type === 'eye' &&
        this.state.targetedItemsIndex !== undefined
      ) {
        translateEyeVertex(
          context.editor,
          this.state.targetedItemsIndex,
          subPoints(imagePoint, previousMouseMovePosition),
          event,
        )
      } else {
        translateVertex(
          context.editor,
          vertexMoving,
          subPoints(imagePoint, previousMouseMovePosition),
          this.state,
          event,
        )
      }
      clearPath2DCache(annotationsMoving[0].annotation.id)
      annotationsMoving[0].annotation.centroid = undefined

      this.state.updateMousePosition(imagePoint)

      // Update rendered annotation to show visual changes during vertex dragging
      context.editor.activeView.updateRenderedAnnotation(annotationsMoving[0].annotation.id)

      return CallbackStatus.Stop // Stop panning completely
    }
    if (previousMouseMovePosition && annotationsMoving.length > 0) {
      // Moving annotation(s) - stop any panning
      if (this.sharedState.isPanning) {
        this.sharedState.isPanning = false
      }

      selectCursor(EditorCursor.Move)

      annotationsMoving.forEach(({ annotation }) => {
        translatePath(context.editor, annotation, subPoints(imagePoint, previousMouseMovePosition))
        clearPath2DCache(annotation.id)
        annotation.centroid = undefined // TODO: mutate?
      })

      this.state.updateMousePosition(imagePoint)

      context.editor.activeView.annotationManager.selectedAnnotations.forEach((annotation) => {
        context.editor.activeView.updateRenderedAnnotation(annotation.id)
      })

      return CallbackStatus.Stop // Stop panning completely
    }

    // If we're actively panning and no annotation movement, allow panning to continue
    if (this.sharedState.isPanning) {
      return CallbackStatus.Continue
    }

    // Handle hover effects and cursor updates (when not moving annotations or panning)
    this.getItemTargetIdFromPoint(context.editor.activeView, imagePoint).then((targetedItemId) => {
      this.state.targetedItemId = targetedItemId
    })

    if (!this.state.targetedItemId) {
      selectCursor(EditorCursor.Move)
      context.editor.activeView.annotationManager.cachedUnhighlightAllAnnotations()
      return
    }

    const itemId = this.state.targetedItemId

    if (context.editor.activeView.annotationManager.isLocked(itemId)) {
      context.editor.activeView.annotationsLayer.updateItemState(itemId, { isLocked: true })
      return
    }

    let annotation = context.editor.activeView.annotationManager.getAnnotation(itemId)

    // Check if multiple annotations are selected - affects vertex highlighting behavior
    const selectedAnnotationCount =
      context.editor.activeView.annotationManager.selectedAnnotations.length

    this.state.targetedItemsIndex = context.editor.activeView.annotationsLayer.hitVertexRegion(
      itemId,
      imagePoint,
    )

    // Only highlight vertices when single annotation is selected
    if (
      this.state.targetedItemId &&
      this.state.targetedItemsIndex !== undefined &&
      selectedAnnotationCount <= 1
    ) {
      context.editor.activeView.annotationManager.highlightVertexIndex(
        this.state.targetedItemsIndex,
      )
    } else {
      context.editor.activeView.annotationManager.unhighlightVertex()
    }

    const annotationWithVertex =
      context.editor.activeView.annotationManager.findAnnotationWithVertexAt(imagePoint)
    const vertex = annotationWithVertex?.vertex

    if (vertex) {
      // on vertex hover set annotation as well
      annotation = annotationWithVertex.annotation
    }

    if (annotation || vertex) {
      selectCursor(EditorCursor.Edit)
    }

    if (vertex) {
      // Only highlight vertices when single annotation is selected
      if (!vertex.isHighlighted && selectedAnnotationCount <= 1) {
        vertex.isHighlighted = true
        // Highlights annotation as well as its vertex
        if (
          annotation &&
          !context.editor.activeView.annotationManager.isHighlighted(annotation.id)
        ) {
          context.editor.activeView.annotationManager.highlightAnnotation(annotation.id)
        }
      } else if (selectedAnnotationCount > 1) {
        // Ensure vertices are unhighlighted when multiple annotations are selected
        vertex.isHighlighted = false
      }
    } else if (annotation) {
      if (!context.editor.activeView.annotationManager.isHighlighted(annotation.id)) {
        context.editor.activeView.annotationManager.highlightAnnotation(annotation.id)
      }
    } else {
      if (context.editor.activeView.annotationManager.highlightedAnnotations.length > 0) {
        context.editor.activeView.annotationManager.cachedUnhighlightAllAnnotations()
      }
      selectCursor(EditorCursor.Move)
    }

    this.state.updateMousePosition(imagePoint)

    context.editor.activeView.annotationManager.selectedAnnotations.forEach((annotation) => {
      context.editor.activeView.updateRenderedAnnotation(annotation.id)
    })
  }

  /**
   * Handles mouse up events in the Edit tool
   *
   * Responsibilities:
   * 1. Completes CMD+drag rectangle selection
   * 2. Finalizes annotation or vertex movement and creates undo actions
   * 3. Resets all tool state for the next interaction
   *
   * Note: Different from other tools, this handles multiple interaction types
   */
  onEnd(context: ToolContext): void {
    // Handle rectangle selection completion
    if (this.state.isDrawingRectangle) {
      this.state.completeRectangleSelection()
      this.state.clearRectangleDrawing(context.editor.activeView)
      return
    }

    // Handle annotation movement completion - create undo action if annotations actually moved
    this.handleAnnotationUpdateEnd(context)

    this.reset(context)
  }

  /**
   * Finalizes annotation movement and creates undo action if movement occurred
   */
  private handleAnnotationUpdateEnd(context: ToolContext): void {
    // Only create undo action if annotations actually moved
    if (!this.state.didMove()) {
      return
    }

    const getAnnotationById = (id: string) =>
      context.editor.activeView.annotationManager.getAnnotation(id)

    const movingAnnotations = this.state.getMovingAnnotationObjects(getAnnotationById)
    const initialData = this.state.getMovingAnnotationsInitialData()
    const currentData = this.state.getMovingAnnotationsCurrentData(getAnnotationById)

    if (movingAnnotations.length > 0 && initialData.length > 0 && currentData.length > 0) {
      const action = updateAnnotationsData(
        context.editor.activeView,
        movingAnnotations,
        initialData,
        currentData,
      )
      context.editor.actionManager.do(action)
    }
  }

  deactivate(context: ToolContext): void {
    this.deactivateToolOptions(context)
  }

  /**
   * Reset state on mouse up, so the next mouse down -> move -> up cycle can be handled
   */
  reset(context: ToolContext): void {
    this.state.reset()
    this.deactivateToolOptions(context)
  }
}

export const editTool = new EditTool()
